import { LinearProgress } from '@material-ui/core';
import BusinessRoutePath from 'app/business-app/BusinessRoutePath';
import {
  useCurrentRepairCapabilities,
  useGetRepairPersonelAddress,
  useIdentity,
  useRepairLocations,
} from 'app/core/api/company/api-company';
import { InsetAreaSpacer } from 'app/shared/components/misc';
import SharedRoutePath from 'app/shared/SharedRoutePath';
import ClassName from 'app/styles/ClassName';
import styledFuncs from 'app/styles/styledFuncs';
import BiMap from 'app/tools/BiMap';
import { useUrlRouteMatch } from 'app/tools/hooks/misc-hooks';
import React from 'react';
import { Redirect } from 'react-router-dom';
import styled from 'styled-components/macro';
import UpdateRepairCapabilities from '../business-profile/UpdateRepairCapabilities';
import UpdateRepairLocations from '../business-profile/UpdateRepairLocations';
import UpdatePersonalDetail from '../business-profile/UpdatePersonalDetail';
import CreatePersonalAddress from '../business-profile/CreatePersonalAddress';

const TopLinearProgress = styled(LinearProgress).attrs({
  className: ClassName.NO_AUTO_MARGIN,
})`
  position: relative;
  top: ${styledFuncs.pad(-1)};
  left: ${styledFuncs.pad(-1)};
  width: calc(100% + ${styledFuncs.pad(2)});
  height: 5px;
  background: transparent;
`;

const pageNames = new BiMap([
  [3, 'create-company-pt1'],
  [4, 'create-company-pt2'],
  [5, 'repair-capabilities'],
  [6, 'repair-locations'],
  [7, 'return-home'],
] as const);
const totalPages = 8;
type Page = ReturnType<typeof pageNames.get>;
type Pages = BiMap<Page, JSX.Element>;
const pages: Pages = new BiMap(
  (
    [
      ['create-company-pt1', UpdatePersonalDetail],
      ['create-company-pt2', CreatePersonalAddress],
      ['repair-capabilities', UpdateRepairCapabilities],
      ['repair-locations', UpdateRepairLocations],
    ] as const
  ).map(([key, C]) => [key, <C key={key} firstTime isIndividual />])
);

export default function CreateIndividualProfile(): JSX.Element {
  const { params } = useUrlRouteMatch();

  const { resource: { company } = {} } = useIdentity();
  const { resource: companyAddress } = useGetRepairPersonelAddress();
  const { resource: repairCapabilities } =
    useCurrentRepairCapabilities();
  const { resource: repairLocations } = useRepairLocations();

  const startPageOpts: { pName: Page; shouldUse: boolean }[] = [
    { pName: 'create-company-pt1', shouldUse: !company },
    { pName: 'create-company-pt2', shouldUse: !companyAddress },
    { pName: 'repair-capabilities', shouldUse: !repairCapabilities },
    { pName: 'repair-locations', shouldUse: !repairLocations },
  ];

  // find the first page that should be used for the start page
  const startPageName = startPageOpts.find(v => v.shouldUse)?.pName;
  // if no initial page suitable, all data has been already entered, and
  // we can allow the user to start at the start to edit their data if needed.
  const initialPageNum =
    pageNames.getKey(startPageName) || pageNames.entries()[0][0];

  const currentPageNum = Number(params.pageNum);
  const currentPageName = pageNames.get(currentPageNum);

  // TODO: remove this when company editing / viewing is supported
  if (company && currentPageName === 'create-company-pt1') {
    return (
      <Redirect
        to={`${BusinessRoutePath.CREATE_PERSONAL_PROFILE_PAGE}/${
          pageNames.getKey('create-company-pt2') || ''
        }`}
      />
    );
  } else if (
    companyAddress &&
    companyAddress?.addrLine1 &&
    currentPageName === 'create-company-pt2'
  ) {
    return (
      <Redirect
        to={`${BusinessRoutePath.CREATE_PERSONAL_PROFILE_PAGE}/${
          pageNames.getKey('repair-capabilities') || ''
        }`}
      />
    );
  }

  // if the current page is missing, go to the initialPage decided above
  if (!currentPageName && Number.isNaN(currentPageNum)) {
    return (
      <Redirect
        to={`${BusinessRoutePath.CREATE_PERSONAL_PROFILE_PAGE}/${initialPageNum}`}
      />
    );
  }

  return (
    <>
      <TopLinearProgress
        variant="determinate"
        value={((currentPageNum + 1) * 100) / (totalPages + 1)}
        color="primary"
      />
      {/* pages.get should now only fail when currentPageName === 'return-home',
          which will then redirect nicely to the home page as intended */}
      {pages.get(currentPageName) || (
        <Redirect to={SharedRoutePath.HOME} />
      )}
      <InsetAreaSpacer pos="bottom" />
    </>
  );
}
